/**
 * Forces a full-document style recalculation on iOS Safari.
 *
 * Ticket rows inside the mobile board list (`MobileList`) can be inserted by React with a
 * genuinely zero-width layout on iOS Safari after a filter or view switch, even though the DOM
 * holds the correct elements with the correct content. This was confirmed on an actual iPhone
 * (iOS 18.7 Safari) with a live DOM probe, not assumed from source reading:
 *
 *   - The row's own computed width was 0px inside a containing block that measured 393px wide —
 *     not legal for a static block box with `width: auto`, which is what makes this a browser bug
 *     rather than an authored CSS mistake.
 *   - `getComputedStyle` showed no transform, no `contain`, no `content-visibility`, no `zoom`,
 *     no stale font metrics — none of the usual suspects for an invisible-but-present element.
 *   - Every element-local fix tried against the row, its wrapper, or the scroll container failed
 *     to restore it: an explicit `width: 100%`, toggling `display`, toggling `overflow`, toggling
 *     `position`, detaching and reattaching the node, even a synthetic pointer/touch/click event
 *     dispatched directly at the collapsed element. Two of those (`display` toggles on elements
 *     that were healthy beforehand) made things *worse* — the toggled element itself got stuck at
 *     zero width afterward, which never recovers, unlike the app's natural bug.
 *   - Every real recovery captured across many traces followed a tap on a Fluent UI control
 *     specifically, never on a plain HTML button — which pointed at something Fluent-specific in
 *     its interaction handling rather than "any touch event". Fluent's components are styled
 *     through Fela, which inserts and rewrites `<style>` rules in `<head>` as components interact.
 *   - Inserting an inert `<style>` rule — forcing the same class of document-level style
 *     invalidation Fela performs — restored the row to a correct width, but only when a layout
 *     read (`getBoundingClientRect`) happened *while the rule was still present*, before it was
 *     removed. This was confirmed twice in the same device trace, verified against the DOM sampler
 *     showing the list held all rows with real height immediately after.
 *
 * A stylesheet mutation invalidates style for the whole document; an element's own `style`
 * property only invalidates that element's subtree, which is why every earlier attempt at this —
 * a `key`-driven remount, a `display` toggle, a compositing hint — either failed or (worse) looked
 * like it worked only because it forced a full re-render rather than actually fixing the layout.
 *
 * The forced read in the middle is not optional. A first version of this function inserted and
 * removed the rule back to back with nothing in between, which measured on-device as a no-op: with
 * no net change to the DOM and nothing reading layout while the rule was active, the browser has no
 * obligation to actually recompute anything before the insert is undone, and evidently didn't. The
 * probe that proved this mechanism always read `getBoundingClientRect` between the insert and the
 * remove — that read is what forces WebKit to resolve the pending recalculation synchronously,
 * with the extra rule still in the cascade, rather than letting the whole insert+remove collapse
 * into nothing.
 */
export const forceStyleRecalc = () => {
  const style = document.createElement('style');
  // The selector must not match anything real; the content of the rule is irrelevant, only the
  // stylesheet mutation itself matters.
  style.textContent = `.__force-style-recalc-${Date.now()} { color: inherit; }`;
  document.head.appendChild(style);
  // Forces the recalculation the insert schedules to actually happen now, while the rule is still
  // present — see above. Any layout read would do; the document root's height is cheap and has no
  // dependency on which element in the page is actually broken. The value itself is unused — only
  // the forced synchronous read matters.
  const _forceReflow = document.documentElement.offsetHeight;
  document.head.removeChild(style);
};
